import { useCallback, useEffect, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { useRequestFormSaveOrNull } from "@RequestFormV2/context/save";
import {
  getDefaultConfirmationDeadline,
  getDefaultQuotingDeadline,
  type TimingSettings,
} from "@RequestFormV2/sections/Provider/utils/providerDefaultTimings";

export function useProviderDefaultTimings(timingSettings: TimingSettings | undefined) {
  const { getValues, setValue } = useFormContext();
  const saveContext = useRequestFormSaveOrNull();

  const quotingDeadlineDate = useWatch({ name: "shipment.quoting_deadline_date" });
  const quotingDeadlineTime = useWatch({ name: "shipment.quoting_deadline_time" });

  const persistField = useCallback(
    (path: string, value: unknown) => {
      saveContext?.saveField(path, value).catch(() => undefined);
    },
    [saveContext],
  );

  useEffect(() => {
    if (!timingSettings) return;
    if (getValues("shipment.quoting_deadline_date")) return;

    const quoting = getDefaultQuotingDeadline();
    setValue("shipment.quoting_deadline_date", quoting.date, { shouldDirty: true });
    setValue("shipment.quoting_deadline_time", quoting.time, { shouldDirty: true });

    const confirmation = getDefaultConfirmationDeadline(timingSettings, {
      useSettingDefault: timingSettings.use_default_confirmation_time,
    });
    setValue("shipment.confirmation_date", confirmation.date, { shouldDirty: true });
    setValue("shipment.confirmation_time", confirmation.time, { shouldDirty: true });

    persistField("shipment.quoting_deadline_date", quoting.date);
    persistField("shipment.quoting_deadline_time", quoting.time);
    persistField("shipment.confirmation_date", confirmation.date);
    persistField("shipment.confirmation_time", confirmation.time);
  }, [timingSettings, quotingDeadlineDate, getValues, setValue, persistField]);

  const skipQuotingResync = useRef(true);

  useEffect(() => {
    if (!timingSettings?.use_default_confirmation_time) return;
    if (!quotingDeadlineDate || !quotingDeadlineTime) return;

    if (skipQuotingResync.current) {
      skipQuotingResync.current = false;
      return;
    }

    const confirmation = getDefaultConfirmationDeadline(timingSettings, { useSettingDefault: true });
    setValue("shipment.confirmation_date", confirmation.date, { shouldDirty: true });
    setValue("shipment.confirmation_time", confirmation.time, { shouldDirty: true });
    persistField("shipment.confirmation_date", confirmation.date);
    persistField("shipment.confirmation_time", confirmation.time);
  }, [timingSettings, quotingDeadlineDate, quotingDeadlineTime, setValue, persistField]);
}
